/**
 * OpeningBalanceNeededBanner - amber prompt listing manual bank accounts
 * flagged `ob_needed` (statement uploaded but no opening balance derivable).
 * Self-fetching; renders nothing when no account needs attention (iter 990).
 *
 * iter1234-hotfix: user asked for the banner to be dismissable on the
 * Bank Feed page. Dismissal is per-account, persisted in sessionStorage
 * so a page reload during the same tab session keeps it hidden but a
 * fresh login (new session) surfaces it again. The account still needs
 * an opening balance - we're not silencing the underlying flag, just
 * this reminder.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from '../ui/button';
import { SetOpeningBalanceDialog } from './SetOpeningBalanceDialog';

const SESSION_KEY = 'akountfly_ob_banner_dismissed_ids';

const loadDismissed = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
};

const saveDismissed = (setLike) => {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify([...setLike]));
  } catch {
    /* noop */
  }
};

export const OpeningBalanceNeededBanner = () => {
  const [accounts, setAccounts] = useState([]);
  const [dialogAccount, setDialogAccount] = useState(null);
  const [dismissed, setDismissed] = useState(() => loadDismissed());

  const fetchFlagged = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/bank-accounts`);
      // Feb 2026 (iter1239) · Use the server-computed
      // `ob_needed_effective` field. The raw `ob_needed` flag stays
      // True even after an OB journal is posted through a different
      // path (manual journal, opening-balance wizard, another
      // statement's derivation, Open Banking auto-post) or when the
      // account was created after the accounting_start_date (OB is
      // trivially zero). The effective field is reconciled per-request
      // against journals_unified + created_at so the banner only
      // surfaces accounts with a real, unmet OB obligation.
      setAccounts((data || []).filter((a) => (
        a.ob_needed_effective === true
        || (a.ob_needed_effective === undefined && a.ob_needed) // graceful fallback for older backend
      )));
    } catch {
      setAccounts([]);
    }
  }, []);

  useEffect(() => { fetchFlagged(); }, [fetchFlagged]);

  const visibleAccounts = useMemo(
    () => accounts.filter((a) => !dismissed.has(a.id)),
    [accounts, dismissed],
  );

  const dismissAll = () => {
    const next = new Set(dismissed);
    accounts.forEach((a) => next.add(a.id));
    setDismissed(next);
    saveDismissed(next);
  };

  const dismissOne = (accountId) => {
    const next = new Set(dismissed);
    next.add(accountId);
    setDismissed(next);
    saveDismissed(next);
  };

  if (visibleAccounts.length === 0) return null;

  return (
    <div
      className="mt-4 bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 relative"
      data-testid="opening-balance-needed-banner"
    >
      <button
        type="button"
        onClick={dismissAll}
        aria-label="Dismiss opening balance reminder"
        title="Hide for this session (the account still needs a balance)"
        className="absolute top-2 right-2 text-amber-700 hover:text-amber-900 p-1 rounded hover:bg-amber-100"
        data-testid="ob-banner-dismiss-btn"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3 pr-8">
        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium text-amber-900">
            Opening balance needed
          </p>
          <p className="text-xs text-amber-800 mt-0.5">
            We couldn&apos;t work out the starting balance from the statement{visibleAccounts.length > 1 ? 's' : ''} you uploaded.
            Without it, your books won&apos;t match the bank.
          </p>
          <div className="mt-2 space-y-1.5">
            {visibleAccounts.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-2 bg-white/70 rounded-md px-3 py-1.5">
                <span className="text-xs font-medium text-amber-900 truncate">
                  {a.bank_name || a.account_name || 'Bank account'}
                  {a.account_number ? ` (...${String(a.account_number).slice(-4)})` : ''}
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={() => setDialogAccount(a)}
                    data-testid={`ob-banner-set-btn-${a.id}`}
                  >
                    Set opening balance
                  </Button>
                  <button
                    type="button"
                    onClick={() => dismissOne(a.id)}
                    aria-label="Hide this reminder"
                    title="Hide this reminder for the session"
                    className="text-amber-700 hover:text-amber-900 p-1 rounded hover:bg-amber-100"
                    data-testid={`ob-banner-dismiss-row-${a.id}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <SetOpeningBalanceDialog
        account={dialogAccount || {}}
        open={!!dialogAccount}
        onClose={() => setDialogAccount(null)}
        onSaved={fetchFlagged}
      />
    </div>
  );
};

export default OpeningBalanceNeededBanner;
